import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';

import { Button } from '@zillow/constellation';
import {
    Col,
    ContentBox,
    Row,
} from '@zillow/zrs-comet';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import PostingActions from 'app/shared/actions/PostingActions';
import UnitCard from 'app/shared/components/postingPath/UnitCard';

const propTypes = {
    canAddUnits: PropTypes.bool,
    canEditTourOverwrite: PropTypes.bool,
    formData: PropTypes.object.isRequired,
    floorPlanIndex: PropTypes.number,
    floorplans: PropTypes.array.isRequired,
    isDisabled: PropTypes.bool,
    isMobile: PropTypes.bool,
    onRemoveUnit: PropTypes.func.isRequired,
    onToggleActive: PropTypes.func.isRequired,
    units: PropTypes.array.isRequired,
    updateFormData: PropTypes.func.isRequired,
    updateParentFloorplanPrice: PropTypes.func.isRequired,
    validateFloorplan: PropTypes.func.isRequired,
    updateStatusMessage: PropTypes.func.isRequired,
};

const defaultProps = {
    canAddUnits: true,
    canEditTourOverwrite: true,
    floorPlanIndex: null,
    isDisabled: false,
    isMobile: false,
};

const UnitsMF = (props) => {
    const {
        canAddUnits,
        canEditTourOverwrite,
        floorPlanIndex,
        floorplans,
        formData,
        isDisabled,
        isMobile,
        onRemoveUnit,
        onToggleActive,
        units,
        updateFormData,
        updateParentFloorplanPrice,
        validateFloorplan,
        updateStatusMessage,
    } = props;

    const dispatch = useDispatch();
    const addDisabled = isDisabled || !canAddUnits;
    const CHUNK_SIZE = 2;
    const unitChunks = [];
    for (let i = 0; i < units.length; i += CHUNK_SIZE) {
        unitChunks.push(units.slice(i, i + CHUNK_SIZE));
    }

    const onAddUnit = () => {
        const canAddUnit = validateFloorplan(floorPlanIndex, true);
        const { floorPlanId } = floorplans[floorPlanIndex];

        if (!canAddUnit) {
            return;
        }

        dispatch(ModalActions.showModal({
            modalType: ModalConstants.EDIT_UNIT_MODAL,
            header: 'Add unit',
            isFluid: true,
            onSubmit: params => dispatch(PostingActions.property.unit.create(floorPlanId, params))
                .then((newUnit) => {
                    dispatch(analyticsUtils.track.GAEventV2(
                        AnalyticsConstants.GA_EVENTS.ADD_UNIT,
                        { label: formData.aliasEncoded }
                    ));
                    const newFloorplans = cloneDeep(floorplans);
                    newFloorplans[floorPlanIndex].units.push(newUnit);
                    updateFormData({ floorplans: newFloorplans });
                    if (newUnit.price) {
                        updateParentFloorplanPrice(floorplans[floorPlanIndex].floorPlanId);
                    }
                    dispatch(ModalActions.hideModal());
                }),
            onCancel: () => dispatch(ModalActions.hideModal()),
        }));
    };

    return (
        <ContentBox padding="xs" highlight="blue">
            {unitChunks.map((unitChunk, chunkIndex) => (
                <Row
                    paddingBottom="sm"
                    key={`unit-row-${chunkIndex}`}
                >
                    {unitChunk.map((unit, unitIndex) => {
                        const realUnitIndex = chunkIndex * CHUNK_SIZE + unitIndex;
                        const isEvenCol = (unitIndex % CHUNK_SIZE === 0);
                        return (
                            <Col
                                paddingBottom={isEvenCol ? 'sm' : ''}
                                mediaQuery={isEvenCol ? { xxl: { paddingBottom: 0 } } : ''}
                                key={`unit-${realUnitIndex}`}
                                xl={6}
                            >
                                <UnitCard
                                    formData={formData}
                                    updateFormData={updateFormData}
                                    updateStatusMessage={updateStatusMessage}
                                    updateParentFloorplanPrice={updateParentFloorplanPrice}
                                    key={`unit-${floorPlanIndex}-${realUnitIndex}`}
                                    id={`unit-${floorPlanIndex}-${realUnitIndex}`}
                                    unitIndex={realUnitIndex}
                                    unit={unit}
                                    isDisabled={isDisabled}
                                    canEditTourOverwrite={canEditTourOverwrite}
                                    isMobile={isMobile}
                                    onToggleActive={() => onToggleActive(!unit.active, floorPlanIndex,
                                        realUnitIndex)}
                                    floorPlanIndex={floorPlanIndex}
                                    onRemoveUnit={() => onRemoveUnit(floorPlanIndex, realUnitIndex)}
                                />
                            </Col>
                        );
                    })}
                </Row>
            ))}
            <Row>
                <Col align="flex-start">
                    <Button
                        buttonType="primary"
                        onClick={onAddUnit}
                        disabled={addDisabled}
                    >
                        + Add unit
                    </Button>
                </Col>

            </Row>
        </ContentBox>
    );
};

UnitsMF.propTypes = propTypes;
UnitsMF.defaultProps = defaultProps;

export default UnitsMF;
